import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Target, Trophy, FileText, ChevronRight, Info, Download } from 'lucide-react';
import { GrasrotandelenBanner } from '../components/GrasrotandelenBanner';

import i18n from '../i18n';
import { useTranslation } from 'react-i18next';
const t = (k, o) => i18n.t(k, o);

const getplans = () => [
  {
    id: 'strategiplan',
    title: t('pg.StrategiSportsplan.ae8017'),
    description: t('pg.StrategiSportsplan.2c24cc'),
    icon: Target,
    path: '/strategi-sportsplan/strategiplan',
    color: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    pdf: {
      url: 'https://customer-assets.emergentagent.com/job_a2119276-b11a-4d90-b267-fc420228fa77/artifacts/nkgbyv1o_Veitvet%20Sportsklubb%20%E2%80%93%20Strategisk%20Plan%202026%E2%80%932031%20%28edited%29.pdf',
      label: t('pg.StrategiSportsplan.79629a'),
    },
  },
  {
    id: 'sportsplan',
    title: t('pg.StrategiSportsplan.5a18c9'),
    description: t('pg.StrategiSportsplan.613f2f'),
    icon: Trophy,
    path: '/strategi-sportsplan/sportsplan',
    color: 'bg-[#CCFF00]/10',
    iconColor: t('pg.StrategiSportsplan.99c73c'),
    pdf: {
      url: 'https://customer-assets.emergentagent.com/job_a2119276-b11a-4d90-b267-fc420228fa77/artifacts/5ymz9ulb_Sportsplan_Veitvet_SK_2026_2031_ny%20versjon.pdf',
      label: t('pg.StrategiSportsplan.02c2fc'),
    },
    subPages: [
      {
        title: t('pg.StrategiSportsplan.8117e9'),
        path: '/strategi-sportsplan/sportsplan/fotball',
      },
    ],
  },
];

export const StrategiSportsplan = () => {
  useTranslation();
  return (
    <div data-testid="strategi-sportsplan-page" className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/styret" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-green-600 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('pg.StrategiSportsplan.6e689e')}
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
              <Target className="w-7 h-7 text-green-600" />
            </div>
            <div>
              <p className="text-green-600 font-medium uppercase tracking-widest mb-1">{t('pg.StrategiSportsplan.d9938a')}</p>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900 dark:text-white">
                {t('pg.StrategiSportsplan.5406bb')}
              </h1>
            </div>
          </div>
          
          <p className="text-slate-600 text-lg">
            {t('pg.StrategiSportsplan.bc6571')}
          </p>
        </div>
      </section>

      {/* Info Banner */}
      <section className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700/40 rounded-xl p-5 sm:p-6 flex items-start gap-4">
            <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-3 text-slate-600 dark:text-slate-300 leading-relaxed">
              <p>
                {t('pg.StrategiSportsplan.8ada3b')} <strong className="text-slate-900 dark:text-white">{t('pg.StrategiSportsplan.99874b')}</strong>{' '}{t('pg.StrategiSportsplan.7d3b2f')}
              </p>
              <p>
                {t('pg.StrategiSportsplan.afd635')}{' '}
                <strong className="text-slate-900 dark:text-white">{t('pg.StrategiSportsplan.75abb3')}</strong>{' '}{t('pg.StrategiSportsplan.6352db')}
              </p>
              <p>
                {t('pg.StrategiSportsplan.448ad0')} <strong className="text-slate-900 dark:text-white">{t('pg.StrategiSportsplan.2c0019')}</strong>{' '}{t('pg.StrategiSportsplan.1e7d5b')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Grid */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {getplans().map((plan) => {
              const IconComponent = plan.icon;
              return (
                <div key={plan.id} className="space-y-3">
                  <Link
                    to={plan.path}
                    data-testid={`plan-${plan.id}`}
                    className="block bg-white rounded-2xl border border-slate-200 hover:border-green-500 hover:shadow-md transition-all duration-300 overflow-hidden group"
                  >
                    <div className="p-6 sm:p-8 flex items-center gap-6">
                      <div className={`w-16 h-16 ${plan.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <IconComponent className={`w-8 h-8 ${plan.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 group-hover:text-green-600 transition-colors mb-2">
                          {plan.title}
                        </h2>
                        <p className="text-slate-500 dark:text-slate-400">{plan.description}</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-slate-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </Link>

                  {/* PDF Download */}
                  {plan.pdf && (
                    <a
                      href={plan.pdf.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`download-${plan.id}-pdf`}
                      className="flex items-center gap-4 bg-white rounded-xl border border-green-500/30 hover:border-green-500 hover:shadow-md p-4 transition-all group"
                    >
                      <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-900 font-medium group-hover:text-green-600 transition-colors truncate">
                          {plan.pdf.label}
                        </p>
                        <p className="text-slate-500 text-sm">{t('pg.StrategiSportsplan.7e38a8')}</p>
                      </div>
                      <span className="text-xs font-medium uppercase bg-slate-100 text-slate-600 px-2 py-1 rounded">{t('pg.StrategiSportsplan.bcd1b6')}</span>
                      <Download className="w-5 h-5 text-slate-400 group-hover:text-green-600 transition-colors flex-shrink-0" />
                    </a>
                  )}
                  
                  {/* Sub-pages */}
                  {plan.subPages && plan.subPages.length > 0 && (
                    <div className="pl-8 sm:pl-12 space-y-2">
                      {plan.subPages.map((subPage, index) => (
                        <Link
                          key={index}
                          to={subPage.path}
                          className="flex items-center gap-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-green-300 p-4 transition-all group"
                        >
                          <div className="w-2 h-2 bg-green-500 rounded-full" />
                          <span className="text-slate-600 group-hover:text-green-600 transition-colors">
                            {subPage.title}
                          </span>
                          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-green-600 ml-auto" />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grasrotandelen Banner */}
      <GrasrotandelenBanner />
    </div>
  );
};

export default StrategiSportsplan;
